import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/config';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRightLeft, 
  Calendar, 
  User, 
  Home, 
  ChevronRight, 
  RefreshCw,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Filter
} from 'lucide-react';


const AdminPassaggiConsegne = () => {
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [properties, setProperties] = useState([]);
  const [managers, setManagers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  
  // Form state
  const [formData, setFormData] = useState({
    property_id: '',
    outgoing_responsible_id: '',
    incoming_responsible_id: '',
    deadline: '',
    notes: ''
  });
  const [creating, setCreating] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem('hw_token');

  const fetchHandovers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/handovers?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHandovers(data.handovers || []);
      }
    } catch (error) {
      console.error('Error fetching handovers:', error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  const fetchPropertiesAndManagers = useCallback(async () => {
    try {
      const [propsRes, managersRes] = await Promise.all([
        fetch(`${API}/api/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/property-managers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (propsRes.ok) {
        const data = await propsRes.json();
        setProperties(data.filter(p => !p.archived));
      }
      if (managersRes.ok) {
        const data = await managersRes.json();
        setManagers(data.filter(m => !m.archived));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchHandovers();
  }, [fetchHandovers]);

  useEffect(() => {
    if (showCreateForm) {
      fetchPropertiesAndManagers();
    }
  }, [showCreateForm, fetchPropertiesAndManagers]);

  const createHandover = async () => {
    if (!formData.property_id || !formData.outgoing_responsible_id || !formData.incoming_responsible_id) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (formData.outgoing_responsible_id === formData.incoming_responsible_id) {
      toast.error('I responsabili devono essere diversi');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/api/handovers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Passaggio consegne creato! Email inviate ai responsabili.');
        setShowCreateForm(false);
        setFormData({ property_id: '', outgoing_responsible_id: '', incoming_responsible_id: '', deadline: '', notes: '' });
        fetchHandovers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella creazione');
      }
    } catch (error) {
      toast.error('Errore di rete');
    } finally {
      setCreating(false);
    }
  };

  const cancelHandover = async (id) => {
    if (!window.confirm('Sei sicuro di voler annullare questo passaggio consegne?')) return;
    
    try {
      const response = await fetch(`${API}/api/handovers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Passaggio annullato');
        fetchHandovers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending_schedule: { label: 'Da fissare', color: 'bg-yellow-100 text-yellow-800' },
      scheduled: { label: 'Programmato', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'In corso', color: 'bg-purple-100 text-purple-800' },
      pending_signatures: { label: 'Firme', color: 'bg-orange-100 text-orange-800' },
      completed: { label: 'Completato', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annullato', color: 'bg-red-100 text-red-800' }
    };
    return configs[status] || configs.pending_schedule;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get current responsible for a property
  const getPropertyCurrentResponsible = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    if (!property) return null;
    return managers.find(m => m.id === property.property_manager_id);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Passaggi Consegne</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestisci i passaggi di consegna tra responsabili
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Attivi</SelectItem>
              <SelectItem value="completed">Completati</SelectItem>
              <SelectItem value="cancelled">Annullati</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchHandovers} variant="outline" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="w-4 h-4 mr-2" /> Nuovo Passaggio
          </Button>
        </div>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <Card className="bg-white border-blue-200 border-2">
          <CardHeader>
            <CardTitle>Nuovo Passaggio Consegne</CardTitle>
            <CardDescription>
              Crea una richiesta di passaggio consegne. I responsabili riceveranno un'email.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Property Selection */}
            <div>
              <label className="text-sm font-medium mb-1 block">Immobile *</label>
              <Select
                value={formData.property_id}
                onValueChange={(v) => {
                  setFormData(prev => ({ ...prev, property_id: v }));
                  // Auto-select current responsible as outgoing
                  const currentResp = getPropertyCurrentResponsible(v);
                  if (currentResp) {
                    setFormData(prev => ({ ...prev, property_id: v, outgoing_responsible_id: currentResp.id }));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona immobile" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.property_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Outgoing Responsible */}
            <div>
              <label className="text-sm font-medium mb-1 block">Responsabile Uscente *</label>
              <Select
                value={formData.outgoing_responsible_id}
                onValueChange={(v) => setFormData(prev => ({ ...prev, outgoing_responsible_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona responsabile uscente" />
                </SelectTrigger>
                <SelectContent>
                  {managers.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.first_name} {m.last_name} ({m.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Incoming Responsible */}
            <div>
              <label className="text-sm font-medium mb-1 block">Responsabile Entrante *</label>
              <Select
                value={formData.incoming_responsible_id}
                onValueChange={(v) => setFormData(prev => ({ ...prev, incoming_responsible_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona responsabile entrante" />
                </SelectTrigger>
                <SelectContent>
                  {managers.filter(m => m.id !== formData.outgoing_responsible_id).map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.first_name} {m.last_name} ({m.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Deadline */}
            <div>
              <label className="text-sm font-medium mb-1 block">Scadenza (opzionale)</label>
              <Input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-1 block">Note (opzionale)</label>
              <Textarea
                placeholder="Note aggiuntive per i responsabili..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                Annulla
              </Button>
              <Button onClick={createHandover} disabled={creating}>
                {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Crea Passaggio
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Da fissare', value: handovers.filter(h => h.status === 'pending_schedule').length, color: 'text-yellow-600' },
          { label: 'Programmati', value: handovers.filter(h => h.status === 'scheduled').length, color: 'text-blue-600' },
          { label: 'In corso', value: handovers.filter(h => ['in_progress', 'pending_signatures'].includes(h.status)).length, color: 'text-purple-600' },
          { label: 'Completati', value: handovers.filter(h => h.status === 'completed').length, color: 'text-green-600' },
          { label: 'Annullati', value: handovers.filter(h => h.status === 'cancelled').length, color: 'text-red-600' }
        ].map((stat, i) => (
          <Card key={i} className="bg-white">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Handovers Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : handovers.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="p-12 text-center">
            <ArrowRightLeft className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun passaggio consegne
            </h3>
            <p className="text-gray-500 mb-4">
              Non ci sono passaggi consegne {statusFilter === 'active' ? 'attivi' : statusFilter}
            </p>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="w-4 h-4 mr-2" /> Crea il primo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Immobile</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Uscente</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Entrante</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Appuntamento</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Stato</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {handovers.map((h) => {
                const statusConfig = getStatusConfig(h.status);
                return (
                  <tr key={h.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-sm">{h.property_name}</p>
                          <p className="text-xs text-gray-500">{h.property_code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{h.outgoing_responsible?.name}</p>
                      {h.outgoing_signature && (
                        <Badge className="text-xs bg-green-100 text-green-700">Firmato</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{h.incoming_responsible?.name}</p>
                      {h.incoming_signature && (
                        <Badge className="text-xs bg-green-100 text-green-700">Firmato</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {h.confirmed_date ? (
                        <div className="text-sm">
                          <p>{formatDate(h.confirmed_date)}</p>
                          <p className="text-xs text-gray-500">{h.confirmed_time}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Non fissato</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/passaggio-consegne/${h.id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!['completed', 'cancelled'].includes(h.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => cancelHandover(h.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPassaggiConsegne;
